import { Box, IconButton, Typography, Button, Stack, Grid2 as Grid } from '@mui/material';
import DataTable from '@/components/Common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import Switch from 'components/Common/Switch';
import TimePicker from 'components/Common/TimePicker';
import { MdOutlineContentCopy } from 'react-icons/md';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { RiDeleteBin6Line } from 'react-icons/ri';
import FormSchemaParser from '@/components/Common/FormSchemaParser';
import Input from '@/components/Common/Input';
import Select from '@/components/Common/Select';

interface ZandaFormProps {
  namePrefix: string;
  control: any;
  getValues: any;
  setValue: any;
  disabled?: boolean;
  configFields: any;
  spacing: string;
  submitForm?: any;
}

const daysOfWeek = [
  { label: 'Monday', value: 'Monday' },
  { label: 'Tuesday', value: 'Tuesday' },
  { label: 'Wednesday', value: 'Wednesday' },
  { label: 'Thursday', value: 'Thursday' },
  { label: 'Friday', value: 'Friday' },
  { label: 'Saturday', value: 'Saturday' },
  { label: 'Sunday', value: 'Sunday' }
];

const defaultWorkingDays = () => {
  const hours: Record<string, { open: boolean; start_time: string; end_time: string; appointment_type_ids: string[] }> =
    {};
  daysOfWeek.forEach((day) => {
    hours[day.value] = {
      open: true,
      start_time: '',
      end_time: '',
      appointment_type_ids: []
    };
  });
  return hours;
};

const normalizeSelectOptions = (options: unknown) => {
  if (Array.isArray(options)) return options;
  return [];
};

const ZandaForm = ({
  namePrefix,
  control,
  disabled,
  configFields,
  spacing,
  submitForm,
  getValues,
  setValue
}: ZandaFormProps) => {
  const providersField = configFields?.find((field: any) => field.key === 'providers');
  const otherFields = configFields?.filter(
    (field: any) => field.key !== 'providers' && field.key !== 'appointment_types'
  );

  const {
    fields: providers,
    append: appendProvider,
    remove: removeProvider
  } = useFieldArray({ control, name: `${namePrefix}.providers` });

  const {
    fields: appointmentTypes,
    append: appendApptType,
    remove: removeApptType
  } = useFieldArray({ control, name: `${namePrefix}.appointment_types` });

  useEffect(() => {
    const existing = getValues(`${namePrefix}.providers`);
    if (!existing || existing.length === 0) {
      appendProvider({ provider: '', working_days: defaultWorkingDays() });
    }
  }, [appendProvider, getValues, namePrefix]);

  useEffect(() => {
    const existing = getValues(`${namePrefix}.appointment_types`);
    if (!existing || existing.length === 0) {
      appendApptType({ name: '', duration: '' });
    }
  }, [appendApptType, getValues, namePrefix]);

  // Derive appointment type options from the current form values so the
  // working-day "appointment_type_ids" multi-select stays in sync.
  const watchedApptTypes = useWatch({ control, name: `${namePrefix}.appointment_types` });

  const appointmentTypeOptions = (watchedApptTypes || []).flatMap((t: any, index: number) => {
    if (!t?.name) return [];
    return [
      {
        value: `appointment_type_${index + 1}`,
        label: t.name + (t.duration ? ` (${t.duration} min)` : '')
      }
    ];
  });

  const providerField = providersField?.group?.find((f: any) => f.key === 'provider');
  const practitionerOptions = normalizeSelectOptions(providerField?.options);

  return (
    <>
      {/* Other scalar fields (location_id, search_availability_window_days) */}
      {otherFields?.length > 0 && (
        <FormSchemaParser
          disabled={disabled}
          fields={otherFields}
          control={control}
          namePrefix={namePrefix}
          gridSize={12}
          spacing={spacing}
          submitForm={(val: any) => submitForm?.(val)}
        />
      )}

      {/* Appointment Types */}
      <Box mt={3}>
        <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography fontSize={16} fontWeight={600}>
            Appointment Types
          </Typography>
          <Button
            variant='contained'
            size='small'
            disabled={disabled}
            onClick={() => appendApptType({ name: '', duration: '' })}
          >
            + Add Appointment Type
          </Button>
        </Stack>

        {appointmentTypes.map((appt: any, aIdx: number) => {
          const apptPrefix = `${namePrefix}.appointment_types.${aIdx}`;
          return (
            <Grid container spacing={2} alignItems='center' mb={2} key={appt.id}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Input
                  name={`${apptPrefix}.name`}
                  control={control}
                  label='Name'
                  placeholder='e.g. Follow-up'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  name={`${apptPrefix}.duration`}
                  control={control}
                  label='Duration (min)'
                  placeholder='e.g. 30'
                  type='number'
                  disabled={disabled}
                  fullWidth
                />
              </Grid>
              {appointmentTypes.length > 1 && !disabled && (
                <Grid size='auto'>
                  <IconButton color='error' onClick={() => removeApptType(aIdx)}>
                    <RiDeleteBin6Line />
                  </IconButton>
                </Grid>
              )}
            </Grid>
          );
        })}
      </Box>

      {/* Providers */}
      {providersField && (
        <Box mt={3}>
          <Typography fontSize={16} fontWeight={600} mb={2}>
            Provider Details
          </Typography>

          {providers.map((_provider: any, index: number) => (
            <ProviderItem
              key={_provider.id}
              index={index}
              control={control}
              remove={removeProvider}
              disabled={disabled}
              getValues={getValues}
              setValue={setValue}
              namePrefix={namePrefix}
              totalProviders={providers.length}
              practitionerOptions={practitionerOptions}
              appointmentTypeOptions={appointmentTypeOptions}
            />
          ))}

          <Box display='flex' justifyContent='center'>
            <Button
              variant='contained'
              disabled={disabled}
              onClick={() => appendProvider({ provider: '', working_days: defaultWorkingDays() })}
            >
              + Add Another Provider
            </Button>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ZandaForm;

// ─── Provider item ────────────────────────────────────────────────────────────

interface ProviderItemProps {
  index: number;
  control: any;
  remove: (index: number) => void;
  disabled?: boolean;
  getValues: any;
  setValue: any;
  namePrefix: string;
  totalProviders: number;
  practitionerOptions: any[];
  appointmentTypeOptions: any[];
}

const ProviderItem = ({
  index,
  control,
  remove,
  disabled,
  getValues,
  setValue,
  namePrefix,
  totalProviders,
  practitionerOptions,
  appointmentTypeOptions
}: ProviderItemProps) => {
  const providerPrefix = `${namePrefix}.providers.${index}`;

  useEffect(() => {
    const existing = getValues(`${providerPrefix}.working_days`);
    if (!existing || Array.isArray(existing) || Object.keys(existing).length === 0) {
      setValue(`${providerPrefix}.working_days`, defaultWorkingDays());
    }
  }, [getValues, providerPrefix, setValue]);

  const columns = useMemo(
    (): ColumnDef<any>[] => [
      {
        header: 'DAY',
        accessorKey: 'label'
      },
      {
        header: 'STATUS',
        cell: (info) => {
          const day = info.row.original.value;
          return <Switch disabled={disabled} name={`${providerPrefix}.working_days.${day}.open`} control={control} />;
        }
      },
      {
        header: 'TIME',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Box display='flex' gap={2} flexWrap='wrap'>
              <TimePicker
                name={`${providerPrefix}.working_days.${day}.start_time`}
                control={control}
                disabled={disabled}
              />
              <TimePicker
                name={`${providerPrefix}.working_days.${day}.end_time`}
                control={control}
                disabled={disabled}
              />
            </Box>
          );
        }
      },
      {
        header: 'APPOINTMENT TYPES',
        cell: (info) => {
          const day = info.row.original.value;
          return (
            <Select
              label='Appointment Types'
              name={`${providerPrefix}.working_days.${day}.appointment_type_ids`}
              control={control}
              disabled={disabled}
              options={appointmentTypeOptions}
              multiple
              fullWidth
            />
          );
        }
      },
      {
        header: 'ACTION',
        cell: (info) => (
          <CopyAction
            dayKey={info.row.original.value}
            fieldName={`${providerPrefix}.working_days`}
            getValues={getValues}
            setValue={setValue}
            disabled={disabled}
          />
        )
      }
    ],
    [control, disabled, getValues, providerPrefix, appointmentTypeOptions, setValue]
  );

  return (
    <Box p={3} mb={3} border='1px solid #E5E7EB' borderRadius='12px'>
      <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography fontWeight={600}>Provider #{index + 1}</Typography>
        {totalProviders > 1 && !disabled && (
          <IconButton color='error' onClick={() => remove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Select
            label='Provider'
            name={`${providerPrefix}.provider`}
            control={control}
            disabled={disabled}
            options={practitionerOptions}
            fullWidth
          />
        </Grid>
      </Grid>

      <Typography fontWeight={500} mt={3} mb={2}>
        Working Days
      </Typography>

      <DataTable data={daysOfWeek} columns={columns} />
    </Box>
  );
};

// ─── Copy action ──────────────────────────────────────────────────────────────

const CopyAction = ({ dayKey, fieldName, getValues, setValue, disabled }: any) => {
  const handleCopy = () => {
    const source = getValues(`${fieldName}.${dayKey}`);

    daysOfWeek.forEach((day) => {
      const target = `${fieldName}.${day.value}`;
      setValue(`${target}.open`, source?.open);
      setValue(`${target}.start_time`, source?.start_time);
      setValue(`${target}.end_time`, source?.end_time);
      setValue(`${target}.appointment_type_ids`, source?.appointment_type_ids || []);
    });
  };

  return (
    <IconButton onClick={handleCopy} disabled={disabled}>
      <MdOutlineContentCopy size={18} />
    </IconButton>
  );
};
